/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import {
  Position,
  StringXPosition,
  StringYPosition,
  Position2D,
  DEFAULT_POSITION_PRIORITY
} from "FloatingWindow/IFloatingWindowConstants";
import { getDocumentViewHandle } from "selectors/documentViewSelectors";

export const careRectBoundary = (positionRect, docViewRects) => {
  if (positionRect.x < docViewRects.left) {
    positionRect.x = docViewRects.left;
  }

  if (positionRect.y < docViewRects.top) {
    positionRect.y = docViewRects.top;
  }

  if (positionRect.x > docViewRects.right - positionRect.width) {
    positionRect.x = docViewRects.right - positionRect.width;
  }

  if (positionRect.y > docViewRects.bottom - positionRect.height) {
    positionRect.y = docViewRects.bottom - positionRect.height;
  }

  return { x: positionRect.x, y: positionRect.y };
};

export const getWindowPositionToRender = (stores, targetRect, windowDimensions, margin = 10) => {
  const docViewRects = getDocumentViewHandle(stores).getBoundingClientRect();

  // Use position priority from targetRect if available, otherwise use default
  // Position priority defines the order in which positions are checked (ABOVE, BELOW, LEFT, RIGHT)
  const priorityToUse = targetRect.positionPriority || DEFAULT_POSITION_PRIORITY;

  let res = {
    x: 0,
    y: 0,
    width: windowDimensions.width,
    height: windowDimensions.height
  };

  // Check space availability for each position in the priority list
  let positionFound = false;
  for (const position of priorityToUse) {
    switch (position) {
      case Position2D.ABOVE:
        if (isSpaceAbove(targetRect, windowDimensions, docViewRects, margin)) {
          res = {
            x: targetRect.x + (targetRect.width - windowDimensions.width) / 2,
            y: targetRect.y - windowDimensions.height - margin,
            width: windowDimensions.width,
            height: windowDimensions.height
          };
          positionFound = true;
        }
        break;
      case Position2D.BELOW:
        if (isSpaceBelow(targetRect, windowDimensions, docViewRects, margin)) {
          res = {
            x: targetRect.x + (targetRect.width - windowDimensions.width) / 2,
            y: targetRect.y + targetRect.height + margin,
            width: windowDimensions.width,
            height: windowDimensions.height
          };
          positionFound = true;
        }
        break;
      case Position2D.LEFT:
        if (isSpaceLeft(targetRect, windowDimensions, docViewRects, margin)) {
          res = {
            x: targetRect.x - windowDimensions.width - margin,
            y: targetRect.y + (targetRect.height - windowDimensions.height) / 2,
            width: windowDimensions.width,
            height: windowDimensions.height
          };
          positionFound = true;
        }
        break;
      case Position2D.RIGHT:
        if (isSpaceRight(targetRect, windowDimensions, docViewRects, margin)) {
          res = {
            x: targetRect.x + targetRect.width + margin,
            y: targetRect.y + (targetRect.height - windowDimensions.height) / 2,
            width: windowDimensions.width,
            height: windowDimensions.height
          };
          positionFound = true;
        }
        break;
      default:
        // render at doc center
        res = {
          x: (docViewRects.width - windowDimensions.width) / 2,
          y: (docViewRects.height - windowDimensions.height) / 2,
          width: windowDimensions.width,
          height: windowDimensions.height
        };
        positionFound = true;
        break;
    }

    if (positionFound) {
      break;
    }
  }

  // If no valid position found based on priority, fallback to center
  if (!positionFound) {
    res = {
      x: (docViewRects.width - windowDimensions.width) / 2,
      y: (docViewRects.height - windowDimensions.height) / 2,
      width: windowDimensions.width,
      height: windowDimensions.height
    };
  }

  return careRectBoundary(res, docViewRects);
};

// Check if space above target rect is available for window placement
export const isSpaceAbove = (targetRect, windowDimensions, docViewRects, margin) => {
  // Calculate the top edge position of the floating window
  const windowTop = targetRect.y - windowDimensions.height - margin;

  // Calculate horizontal position (centered by default)
  const windowLeft = targetRect.x + (targetRect.width - windowDimensions.width) / 2;
  const windowRight = windowLeft + windowDimensions.width;

  // Check if window would be within viewport bounds
  return (
    windowTop >= docViewRects.top && windowRight - windowLeft <= docViewRects.width // Top edge in view
  );
};

// Check if space below target rect is available for window placement
export const isSpaceBelow = (targetRect, windowDimensions, docViewRects, margin) => {
  // Calculate the top edge position of the floating window
  const windowTop = targetRect.y + targetRect.height + margin;
  const windowBottom = windowTop + windowDimensions.height;

  // Calculate horizontal position (centered by default)
  const windowLeft = targetRect.x + (targetRect.width - windowDimensions.width) / 2;
  const windowRight = windowLeft + windowDimensions.width;

  // Check if window would be within viewport bounds
  return (
    windowBottom <= docViewRects.bottom && windowRight - windowLeft <= docViewRects.width // Bottom edge in view
  );
};

// Check if space to the left of target rect is available for window placement
export const isSpaceLeft = (targetRect, windowDimensions, docViewRects, margin) => {
  // Calculate the left edge position of the floating window
  const windowLeft = targetRect.x - windowDimensions.width - margin;

  // Calculate vertical position (centered by default)
  const windowTop = targetRect.y + (targetRect.height - windowDimensions.height) / 2;
  const windowBottom = windowTop + windowDimensions.height;

  // Check if window would be within viewport bounds
  return (
    windowLeft >= docViewRects.left && windowBottom - windowTop <= docViewRects.height // Left edge in view
  ); // Top edge in view
};

// Check if space to the right of target rect is available for window placement
export const isSpaceRight = (targetRect, windowDimensions, docViewRects, margin) => {
  // Calculate the left edge position of the floating window
  const windowLeft = targetRect.x + targetRect.width + margin;
  const windowRight = windowLeft + windowDimensions.width;

  // Calculate vertical position (centered by default)
  const windowTop = targetRect.y + (targetRect.height - windowDimensions.height) / 2;
  const windowBottom = windowTop + windowDimensions.height;

  // Check if window would be within viewport bounds
  return (
    windowRight <= docViewRects.right && // Right edge in view
    windowTop >= docViewRects.top && // Top edge in view
    windowBottom <= docViewRects.bottom
  ); // Bottom edge in view
};

export const getRectsInPreviewViewSpace = (stores, rect) => {
  if (!isFinite(rect.x) || !isFinite(rect.y)) {
    return rect;
  }
  const docViewRects = getDocumentViewHandle(stores).getBoundingClientRect();
  let xInViewSpace = rect.x + docViewRects.left;
  let yInViewSpace = rect.y + docViewRects.top;

  return { ...rect, x: xInViewSpace, y: yInViewSpace };
};

export const isComponentInView = (stores, { x, y, width, height }) => {
  const documentViewHandle = getDocumentViewHandle(stores);
  const docViewRects = documentViewHandle.getBoundingClientRect();
  const componentHeight = height;
  const componentWidth = width;
  return (
    x >= docViewRects.left &&
    y >= docViewRects.top &&
    x <= docViewRects.right - componentWidth &&
    y <= docViewRects.bottom - componentHeight
  );
};

const isXMovementAllowed = (stores, x, width) => {
  const documentViewHandle = getDocumentViewHandle(stores);
  const docViewRects = documentViewHandle.getBoundingClientRect();
  return x >= docViewRects.left && x < docViewRects.right - width;
};

const isYMovementAllowed = (stores, y, height) => {
  const documentViewHandle = getDocumentViewHandle(stores);
  const docViewRects = documentViewHandle.getBoundingClientRect();
  return y >= docViewRects.top && y < docViewRects.bottom - height;
};

export const calculateNewPosition = (
  stores,
  initialCursorPos,
  finalCursorPos,
  prevFloatingWindowPosition,
  windowDimensions
) => {
  const diff = {
    x: finalCursorPos.x - initialCursorPos.current.x,
    y: finalCursorPos.y - initialCursorPos.current.y
  };
  if (
    !isComponentInView(stores, {
      x: prevFloatingWindowPosition.x + diff.x,
      y: prevFloatingWindowPosition.y + diff.y,
      width: windowDimensions.width,
      height: windowDimensions.height
    })
  ) {
    let x = prevFloatingWindowPosition.x;
    let y = prevFloatingWindowPosition.y;
    const documentViewHandle = getDocumentViewHandle(stores);
    const docViewRects = documentViewHandle.getBoundingClientRect();
    if (isXMovementAllowed(stores, x + diff.x, windowDimensions.width)) {
      initialCursorPos.current.x = finalCursorPos.x;
      x += diff.x;
    } else if (x + diff.x >= docViewRects.right - windowDimensions.width) {
      x = docViewRects.right - windowDimensions.width;
    } else {
      x = docViewRects.left;
    }

    if (isYMovementAllowed(stores, prevFloatingWindowPosition.y + diff.y, windowDimensions.height)) {
      initialCursorPos.current.y = finalCursorPos.y;
      y += diff.y;
    } else if (y + diff.y >= docViewRects.bottom - windowDimensions.height) {
      y = docViewRects.bottom - windowDimensions.height;
    } else {
      y = docViewRects.top;
    }
    return { x, y };
  }
  initialCursorPos.current = finalCursorPos;
  return { x: prevFloatingWindowPosition.x + diff.x, y: prevFloatingWindowPosition.y + diff.y };
};

export const getPositionFromTargetPointInViewSpace = (x, y, docViewRects, windowDimensions, marginFromNearestEdge) => {
  let position = { x: 0, y: 0 };

  if (typeof x === "number" && typeof y === "number") {
    position.x =
      x < docViewRects.left + marginFromNearestEdge
        ? docViewRects.left + marginFromNearestEdge
        : x > docViewRects.right - windowDimensions.width - marginFromNearestEdge
        ? docViewRects.right - windowDimensions.width - marginFromNearestEdge
        : x;

    position.y =
      y < docViewRects.top + marginFromNearestEdge
        ? docViewRects.top + marginFromNearestEdge
        : y > docViewRects.bottom - windowDimensions.height - marginFromNearestEdge
        ? docViewRects.bottom - windowDimensions.height - marginFromNearestEdge
        : y;

    return position;
  }

  if (x === StringXPosition.LEFT) {
    position.x = docViewRects.left + marginFromNearestEdge;
  } else if (x === StringXPosition.RIGHT) {
    position.x = docViewRects.right - windowDimensions.width - marginFromNearestEdge;
  } else if (x === StringXPosition.CENTER) {
    position.x = (docViewRects.width - windowDimensions.width) / 2;
  }

  if (y === StringYPosition.TOP) {
    position.y = docViewRects.top + marginFromNearestEdge;
  } else if (y === StringYPosition.BOTTOM) {
    position.y = docViewRects.bottom - windowDimensions.height - marginFromNearestEdge;
  } else if (y === StringYPosition.CENTER) {
    position.y = (docViewRects.height - windowDimensions.height) / 2;
  }

  return position;
};

const _validateTargetPoint = targetPoint =>
  [StringXPosition.LEFT, StringXPosition.RIGHT, StringXPosition.CENTER].includes(targetPoint.x) &&
  [StringYPosition.TOP, StringYPosition.BOTTOM, StringYPosition.CENTER].includes(targetPoint.y);

export const convertTargetPointStringToNumber = (docViewRects, targetPoint, windowDimensions) => {
  if (typeof targetPoint.x === "number" && typeof targetPoint.y === "number") {
    return { x: targetPoint.x, y: targetPoint.y };
  }

  if (!_validateTargetPoint(targetPoint)) {
    throw new Error("Invalid target point");
  }

  const positionInNumber = { x: 0, y: 0 };
  switch (targetPoint.x) {
    case StringXPosition.LEFT:
      positionInNumber.x = docViewRects.left;
      break;
    case StringXPosition.RIGHT:
      positionInNumber.x = docViewRects.right - windowDimensions.width;
      break;
    case StringXPosition.CENTER:
      positionInNumber.x = (docViewRects.width - windowDimensions.width) / 2;
      break;
    default:
      positionInNumber.x = targetPoint.x;
  }

  switch (targetPoint.y) {
    case StringYPosition.TOP:
      positionInNumber.y = docViewRects.top;
      break;
    case StringYPosition.BOTTOM:
      positionInNumber.y = docViewRects.bottom - windowDimensions.height;
      break;
    case StringYPosition.CENTER:
      positionInNumber.y = (docViewRects.height - windowDimensions.height) / 2;
      break;
    default:
      positionInNumber.y = targetPoint.y;
  }

  return positionInNumber;
};
